import { TLocale } from '@@config/locale/';
import { sanitizeReportNumber } from '@@utils/utils';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Form, useSubmit } from 'react-router';
import i18n from './i18n.json';

type TReportLookupFormProps = {
    readonly reportNo: string;
    readonly locale: TLocale;
};

const LookupForm: React.FC<TReportLookupFormProps> = ({ reportNo, locale }) => {
    const labels = i18n[locale];

    const [reportNumber, setReportNumber] = useState<string>(reportNo);
    const [validationMessage, setValidationMessage] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const reportInput = useRef<HTMLInputElement>(null);
    const submitBtn = useRef<HTMLButtonElement>(null);

    const submit = useSubmit();

    useEffect(() => {
        if (reportNumber) {
            reportInput.current?.focus();
        }
    }, []);

    function handleInvalid(event: FormEvent): void {
        const target = event.target as HTMLInputElement;

        if (!target.validity.valid) {
            target.setCustomValidity(
                labels?.['INPUT_INVALID_MESSAGE']?.label ?? 'Please provide a valid report number'
            );
        } else {
            target.setCustomValidity('');
        }

        setValidationMessage(target.validationMessage);
        setIsSubmitting(false);
    }

    function handleInput(event: FormEvent): void {
        const target = event.target as HTMLInputElement;
        setReportNumber(target.value);

        target.setCustomValidity('');
        setValidationMessage('');
        setIsSubmitting(false);
    }

    function handleSubmit(event: FormEvent): void {
        event.preventDefault();
        const form = event.target as HTMLFormElement;
        const isValid: boolean = form.checkValidity();

        if (!isValid) {
            return;
        }

        if (reportInput.current) {
            reportInput.current.setCustomValidity('');
        }

        if (submitBtn.current) {
            submitBtn.current.setAttribute('disabled', 'true');
        }

        setIsSubmitting(true);

        submit(
            [
                ['locale', locale],
                ['reportno', sanitizeReportNumber(reportNumber)],
            ],
            { method: 'GET', action: '/report-check' }
        );
    }

    const hasError = Boolean(validationMessage);
    const isSuccess = isSubmitting && !hasError;

    const inputStateClasses = hasError
        ? 'pl-3 pr-9 border-[var(--color-status-error)] focus-visible:outline-2 focus-visible:outline-[color:var(--color-status-error)]'
        : isSuccess
          ? 'pl-3 pr-9 bg-white border-[var(--color-status-success)] focus-visible:outline-2 focus-visible:outline-[color:var(--color-status-success)]'
          : 'px-3 border-[var(--bg-medium-grey)] focus-visible:outline-2 focus-visible:outline-[color:var(--text-blue)]';

    return (
        <Form
            onSubmit={handleSubmit}
            className='callout-container px-4 py-6 shadow-sm'
            style={{ borderRadius: 5 }}
            noValidate
        >
            <div className='grid grid-cols-1 gap-x-4 bg-transparent py-2 md:grid-cols-3 md:items-center'>
                <label
                    htmlFor='reportno'
                    className='mb-0 flex justify-center text-xs font-semibold text-black md:justify-end'
                >
                    {labels?.['LOOK_UP_REPORT']?.label ?? 'Look Up a Report'}
                </label>

                <div className={`relative ${validationMessage ? 'mb-6' : 'mb-2'} md:mb-0`}>
                    <input
                        ref={reportInput}
                        type='text'
                        className={`w-full rounded-none border py-1.5 text-[13px] ${inputStateClasses}`}
                        id='reportno'
                        name='reportno'
                        placeholder={labels?.['ENTER_REPORT_NUMBER']?.label ?? 'Enter Report Number'}
                        pattern='^(\d+-?\d+|[a-zA-Z0-9]{4,5})$'
                        minLength={4}
                        maxLength={14}
                        value={reportNumber}
                        onInvalid={handleInvalid}
                        onInput={handleInput}
                        required
                    />
                    {hasError && (
                        <svg
                            className='pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--color-status-error)]'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            aria-hidden='true'
                        >
                            <circle cx='12' cy='12' r='10' />
                            <line x1='12' y1='8' x2='12' y2='12' />
                            <line x1='12' y1='16' x2='12.01' y2='16' />
                        </svg>
                    )}
                    {isSuccess && (
                        <svg
                            className='pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--color-status-success)]'
                            viewBox='0 0 16 16'
                            fill='currentColor'
                            aria-hidden='true'
                        >
                            <path d='M13.78 4.22a.75.75 0 0 1 0 1.06l-6.5 6.5a.75.75 0 0 1-1.06 0l-3-3a.75.75 0 1 1 1.06-1.06L6.75 10.19l5.97-5.97a.75.75 0 0 1 1.06 0z' />
                        </svg>
                    )}
                    {validationMessage && (
                        <span className='absolute left-0 top-full mt-0.5 text-xs text-[color:var(--color-status-error)]'>
                            {validationMessage}
                        </span>
                    )}
                </div>

                <div className='flex justify-center md:justify-start'>
                    <button
                        ref={submitBtn}
                        type='submit'
                        className='btn search-btn rounded bg-black text-white hover:text-white'
                    >
                        {labels?.['LOOK_UP']?.label ?? 'Look Up'}
                    </button>
                </div>
            </div>
        </Form>
    );
};

export default LookupForm;
export type { TReportLookupFormProps };
